// InviteAcceptPage — rota /invite/:token. Aceita convite, escolhe avatarTone, define senha.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { Avatar, type AvatarTone } from '@/components/ui/Avatar';
import { ArrowRightIcon } from '@/components/icons';
import { ROUTES } from '@/app/router/paths';
import { useAcceptInviteMutation } from '../api';
import { parseAuthError } from '../errors';
import { LogoMark } from './shared/LogoMark';
import styles from './InviteAcceptPage.module.css';

const TONES: AvatarTone[] = [1, 2, 3, 4, 5, 6, 7, 8];

const schema = z
  .object({
    name: z.string().min(2, 'Diga seu nome.'),
    password: z.string().min(6, 'Mínimo 6 caracteres.'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'As senhas não coincidem.',
  });

type FormData = z.infer<typeof schema>;

export default function InviteAcceptPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [tone, setTone] = useState<AvatarTone>(4);
  const [accept, { isLoading, error }] = useAcceptInviteMutation();
  const toast = useToast();

  useEffect(() => {
    if (error)
      toast.danger({ title: 'Não foi possível aceitar', description: parseAuthError(error) });
  }, [error, toast]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const previewName = watch('name') || 'Você';

  const onSubmit = handleSubmit(async (v) => {
    if (!token) return;
    try {
      await accept({ token, name: v.name, password: v.password, avatarTone: tone }).unwrap();
      navigate(ROUTES.root, { replace: true });
    } catch {
      /* mostra erro */
    }
  });

  if (!token) {
    return (
      <main>
        <LogoMark />
        <h1 className={styles.title}>Convite inválido</h1>
        <p className={styles.subtitle}>O link de convite está incompleto.</p>
      </main>
    );
  }

  return (
    <main aria-labelledby="invite-title">
      <LogoMark />
      <h1 id="invite-title" className={styles.title}>
        Aceitar convite
      </h1>
      <p className={styles.subtitle}>Defina sua senha e escolha um avatar.</p>

      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <Input
          label="Seu nome"
          autoComplete="name"
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Nova senha"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <Input
          label="Confirmar senha"
          type="password"
          autoComplete="new-password"
          error={errors.confirm?.message}
          {...register('confirm')}
        />

        <div className={styles.avatarPicker}>
          <span className={styles.pickerLabel}>Cor do avatar</span>
          <div className={styles.avatarPreview}>
            <Avatar name={previewName} tone={tone} size="lg" />
          </div>
          <div className={styles.toneGrid} role="radiogroup" aria-label="Escolher cor do avatar">
            {TONES.map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={tone === t}
                aria-label={`Tom ${t}`}
                data-active={tone === t || undefined}
                className={styles.toneSwatch}
                onClick={() => setTone(t)}
              >
                <Avatar name={previewName} tone={t} size="sm" />
              </button>
            ))}
          </div>
        </div>

        <Button
          type="submit"
          size="lg"
          loading={isLoading}
          trailingIcon={<ArrowRightIcon size={18} />}
        >
          Aceitar e entrar
        </Button>
      </form>
    </main>
  );
}
